import * as React from 'react'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import styles from './index.scss'
import {
  Form,
  Input,
  Table,
  Button,
  Row,
  Col,
  Select,
  Icon,
  InputNumber,
  Modal,
  message,
} from 'antd'
import { debounce, findLastIndex, findIndex, cloneDeep } from 'lodash'
import RichEditor from 'components/RichEditor'
import { ComponentExt } from 'util/reactExt'
import { taskType, checkType } from 'constants/index'
import { FormComponentProps } from 'antd/lib/form'
import { withPopover } from 'components/HOC/AntdWrapper'
import { levelStrategies } from 'store/LevelStore'
import examIcon from 'assets/exam.svg'
import videoIcon from 'assets/video.svg'
import textIcon from 'assets/text.svg'

let isFirstEnter = true
const videoTypeIconMap = {
  [taskType.video]: videoIcon,
  [taskType.example]: examIcon,
  [taskType.exam]: examIcon,
  [taskType.text]: textIcon,
}
const Fragment = React.Fragment
const FormItem = Form.Item
const Option = Select.Option
const confirm = Modal.confirm
const basicLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 4 },
  },
}
const leftWithLabelLayout = {
  ...basicLayout,
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
  },
}
const leftEditorLayout = {
  ...basicLayout,
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 22 },
  },
}
const rightWithLabelLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
  },
}
const leftWithouLabelLayout = {
  ...basicLayout,
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 21, offset: 1 },
  },
}

interface IConfigCardProps extends FormComponentProps {
  data: any
  order: number
  currentLessonId: number
  levelStore?: any
  globalStore?: any
}
interface IConfigState {
  deleteId: number
  examList: any
  videoList: any
  examKeyword: string
  videoKeyword: string
  loading: boolean
}

@inject('globalStore', 'levelStore')
@observer
class JZConfigCard extends ComponentExt<IConfigCardProps, IConfigState> {
  state = {
    deleteId: -1,
    examList: null,
    videoList: null,
    examKeyword: '',
    videoKeyword: '',
    loading: false,
  }

  getExamList = debounce((payload, mode?) => {
    this.setState({
      loading: true,
    })
    this.api.level
      .getSelectableExamList(payload)
      .then(res => {
        if (mode === 'append') {
          const {
            examList: { data },
          } = this.state
          this.setState({
            examList: {
              meta: res.meta,
              data: data.concat(res.data),
            },
            loading: false,
          })
        } else {
          this.setState({
            examList: res,
            loading: false,
          })
        }
      })
      .catch(err => {
        this.$message.error(err)
        this.setState({
          loading: false,
        })
      })
  }, 1000)

  getVideoList = debounce((payload, mode?) => {
    this.setState({
      loading: true,
    })
    this.api.level
      .getVideoList(payload)
      .then(res => {
        if (mode === 'append') {
          const {
            videoList: { data },
          } = this.state
          this.setState({
            videoList: {
              meta: res.meta,
              data: data.concat(res.data),
            },
            loading: false,
          })
        } else {
          this.setState({
            videoList: res,
            loading: false,
          })
        }
      })
      .catch(err => {
        this.$message.error(err)
        this.setState({
          loading: false,
        })
      })
  }, 1000)

  updateCheck = debounce((content?: any): void => {
    const {
      order,
      levelStore: { checkTemplate, updateCheck },
      form,
    } = this.props
    const { checks } = checkTemplate
    const values: any = form.getFieldsValue()
    const check = {
      ...checks.data[order],
      ...values,
      group_id: values.group_id,
      configs: {
        video_count: values.video_count,
        video_time_length: values.video_time_length,
      },
      tasks: [...checks.data[order].tasks],
    }

    delete check.text

    if (check.type === checkType.text && isFirstEnter) {
      isFirstEnter = false
      return
    }

    if (check.type === checkType.text && typeof content === 'string') {
      check.tasks[0] = {
        name: '文字关',
        type: taskType.text,
        text: {
          text: content,
        },
      }
    }

    if (!values.group_id) {
      delete check.group_id
    }
    updateCheck(check, order)
  }, 600)

  componentWillUnmount() {
    isFirstEnter = true
  }

  updateCheckGroup = (groupId): void => {
    const {
      order,
      levelStore: { checkTemplate, setSelectedGroup },
    } = this.props
    const { checks } = checkTemplate
    const check = {
      ...checks.data[order],
      group_id: groupId,
    }

    if (!groupId) {
      delete check.group_id
    }

    this.handleReorderGroup(groupId, check)
    setSelectedGroup(groupId)
  }

  handleReorderGroup = (groupId, check) => {
    const {
      levelStore: {
        reorderCheck,
        updateCheck,
        checkTemplate: {
          checks: { data },
          check_groups,
        },
      },
      order,
    } = this.props
    /*
      关卡被添加入的分组：groupId
      所有分组：check_groups
      所有关卡：checks.data
      目标移动位置：target
     关卡移动目标位置 target 的计算规则为:
     1. 首先在 checks.data 中寻找存在 group 值的关卡的最后一个下标
     2. 如果找到该下标则直接将当前关卡排序到最后一个下标的关卡后面
     3. 如果没有找到，则表示当前关卡被添加到新的分组，需要进一步判断该 groupId 位于 check_groups 中的位置
     4. 再从 checks.data 中找到 位于 groupId 前一位 groupId 的 checks.data 的最后一个下标，如果找到则将关卡插入其后，如果没有找到则插入 checks.data 的第一位
     */
    let target = 0
    const lastCheckIndexOfMatchedGroupId = findLastIndex(data, {
      group_id: groupId,
    })

    // 是否为关卡第一次设置当前分组
    if (lastCheckIndexOfMatchedGroupId < 0) {
      // 之前是否为其他关卡设置过其他分组
      const isExistOtherCheck = findIndex(data, 'group_id') < 0 ? false : true
      if (!isExistOtherCheck) {
        target = 0
      } else {
        const currentCheckGroupIndex = findIndex(
          check_groups,
          (group: any) => group.id === groupId,
        ) // 当前关卡所选的分组位于 checkGroup 中的下标
        const matchedGroupIndex = findLastIndex(data, (item: any) => {
          const checkGroupIndex = findIndex(
            check_groups,
            (group: any) => group.id === item.group_id,
          ) // 遍历所有的关卡，找出关卡在 checkGroup 中的下标

          return checkGroupIndex < 0
            ? false
            : currentCheckGroupIndex - checkGroupIndex >= 0
            ? true
            : false
        })

        const currentCheckPosition = findLastIndex(
          data,
          (item: any) => item.id === check.id,
        ) // 找出当前需要重排序的关卡的下标

        if (currentCheckPosition - matchedGroupIndex <= 0) {
          target = matchedGroupIndex
        } else {
          target = matchedGroupIndex + 1
        }
      }
    } else {
      // 当前分组不是第一次分配，在当前分组的最后一个关卡后添加当前关卡
      const currentCheckPosition = findLastIndex(
        data,
        (item: any) => item.id === check.id,
      )
      // 当前关卡初始位置是在目标移动位置的右侧
      if (currentCheckPosition - lastCheckIndexOfMatchedGroupId > 0) {
        target = lastCheckIndexOfMatchedGroupId + 1
      } else {
        target = lastCheckIndexOfMatchedGroupId
      }
    }
    reorderCheck(target, order)
    updateCheck(check, target)
  }

  setCurrentCheck = (check: any, index: number) => {
    const {
      levelStore: { setCurrentCheck },
    } = this.props
    setCurrentCheck(check, index)
  }

  onPopupScroll = (mode, event): any => {
    const el = event.target
    const isBottom = el.scrollHeight - el.scrollTop === el.clientHeight

    if (mode === 'exam') {
      const {
        examList: {
          meta: { pagination },
        },
      } = this.state
      if (isBottom) {
        if (!pagination.next) {
          this.getExamList(
            {
              page: pagination.current_page + 1,
              per_page: pagination.per_page,
            },
            'append',
          )
        }
      }
    } else {
      const {
        videoList: {
          meta: { pagination },
        },
      } = this.state
      if (isBottom) {
        if (!pagination.next) {
          this.getVideoList(
            {
              page: pagination.current_page + 1,
              per_page: pagination.per_page,
            },
            'append',
          )
        }
      }
    }
  }

  onSelectSearch = (type, keyword): void => {
    // 拉取数据到选择框
    let payload: any = {
      page: 0,
      per_page: 10,
    }

    const {
      levelStore: {
        checkTemplate: { project_id },
      },
    } = this.props
    if (type === 'exam') {
      payload = {
        ...payload,
        keyword,
        project_id,
      }
      this.getExamList(payload)
    } else {
      payload = {
        ...payload,
        name: keyword,
        project_id,
      }
      this.getVideoList(payload)
    }
  }

  addVideoTask = (type, value) => {
    const { videoList } = this.state
    const {
      levelStore: {
        addTask,
        checkTemplate: { project_id, checks },
      },
      order,
    } = this.props
    const { tasks } = checks && checks.data[order]

    let task
    /**
     * 综合关中，如果添加的是视频任务，则直接新增关卡任务；
     * 如果是增加试题，则判断是否为连续添加试题，如果是则将试题追加到当前试题任务里的 example_titles，否则新增试题任务
     */
    if (type === 'video') {
      const video = videoList.data.find(item => item.id === value)
      const videoTimeLimit = this.props.form.getFieldValue('video_time_length')
      if (videoTimeLimit > 0) {
        const videoTasks = tasks.filter(item => item.type === taskType.video)
        const totalVideoTime =
          videoTasks && videoTasks.length > 0
            ? videoTasks.reduce(
                (total, item) => (total += item.video.course_time),
                0,
              )
            : 0
        if (totalVideoTime + video.course_time > videoTimeLimit) {
          this.$message.warning('综合关视频总时长超过限制时长')
          return false
        }
      }

      task = {
        id: video.id,
        type: taskType.video,
        name: video.title,
        video: {
          project_id,
          article_id: video.id,
          type: video.type,
          title: video.title,
          img_url: video.img_url,
          course_time: video.course_time,
        },
      }
    } else {
      let lastTaskOfVideoCheck = null
      if (tasks.length > 0) {
        lastTaskOfVideoCheck = tasks[tasks.length - 1]
      }
      const isAddExamTask =
        tasks.length === 0 ||
        (lastTaskOfVideoCheck &&
          (lastTaskOfVideoCheck.type === taskType.video ||
            lastTaskOfVideoCheck.type === taskType.text))
      const { examList } = this.state
      const exam = examList && examList.data.find(item => item.id === value)
      if (isAddExamTask) {
        task = {
          id: exam.id,
          name: exam.content,
          type: taskType.example,
          example_titles: [
            {
              id: exam.id,
              content: exam.content,
            },
          ],
        }
      } else {
        task = {
          ...lastTaskOfVideoCheck,
          example_titles: lastTaskOfVideoCheck.example_titles.concat({
            id: exam.id,
            content: exam.content,
          }),
        }
      }
    }

    addTask(order, task)
  }

  addExamOrRquiredTask = (type, value) => {
    const { data } = this.props
    const { examList } = this.state
    const {
      levelStore: {
        addTask,
        checkTemplate: { checks },
      },
      order,
    } = this.props
    let task
    const { tasks } = checks && checks.data[order]
    const exam = examList && examList.data.find(item => item.id === value)

    /**
     * 任务类型为习题或考试时，需要先判断一下当前关卡下的任务是否已经存在，如果存在则将后续添加的试题放入 exam_titles 或 example_titles
     */
    if (data && data.type === checkType.example) {
      if (tasks.length > 0) {
        task = {
          ...tasks[0],
          example_titles: tasks[0].example_titles.concat({
            id: exam.id,
            content: exam.content,
          }),
        }
      } else {
        task = {
          id: exam.id,
          name: exam.content,
          type: taskType.example,
          example_titles: [
            {
              id: exam.id,
              content: exam.content,
            },
          ],
        }
      }
    } else {
      if (tasks.length > 0) {
        task = {
          ...tasks[0],
          exam_titles: tasks[0].exam_titles.concat({
            id: exam.id,
            content: exam.content,
          }),
        }
      } else {
        task = {
          id: exam.id,
          name: exam.content,
          type: taskType.exam,
          exam_titles: [
            {
              id: exam.id,
              content: exam.content,
            },
          ],
        }
      }
    }

    addTask(order, task)
  }

  addTextTask = (): void => {
    const {
      levelStore: { addTask },
      order,
      form,
    } = this.props

    const name = form.getFieldValue('guide')
    if (name === '') {
      message.error('请输入内容!')
      return
    }
    const id = Date.now().toString()
    const task = {
      id: '1' + id.substr(id.length - 4),
      type: taskType.text,
      name,
      text: {
        text: name,
      },
    }

    addTask(order, task)
    form.setFieldsValue({ guide: '' })
  }

  onSelect = (type: any, value: any): void => {
    const { data } = this.props
    if (data && data.type === checkType.integration) {
      this.addVideoTask(type, value)
    } else {
      this.addExamOrRquiredTask(type, value)
    }
  }

  onDeselect = (id: any) => {
    this.deleteTask(id)
  }

  reorderTask = (id: number, mode) => {
    const {
      levelStore: { reorderTask },
      order,
    } = this.props
    reorderTask(order, id, mode === 'up' ? -1 : 1)
  }

  deleteTask = (id: number) => {
    const {
      levelStore: { deleteTask },
      order,
    } = this.props
    deleteTask(order, id)
  }

  reorderCheck = (target: number, source: number): void => {
    const {
      levelStore: { reorderCheck, checkTemplate, updateCheck },
    } = this.props
    const {
      checks: { data },
    } = checkTemplate

    if (data[target].group_id !== data[source].group_id) {
      confirm({
        title: '清空分组提醒',
        okType: 'danger',
        content:
          '因位置调整，该关卡即将移入 “分组名称” ，是否确认，选否则依然保留在原有分组？',
        onOk: () => {
          const check = {
            ...data[source],
            group_id: data[target].group_id,
          }
          updateCheck(check, source)
          reorderCheck(target, source)
        },
      })
    } else {
      reorderCheck(target, source)
    }

    // 判断当前关卡的 group_id 和 目前关卡的 group_id 是否一致
    // 如果一致则移动，如果不一致则弹窗弹窗询问是否重置分组
    // 如果是的话，就将将当前关卡的 group_id 设为新的 id;
  }

  deleteCheck = async order => {
    const {
      levelStore: { deleteCheck, checkTemplate, getEntityList, setTasks },
    } = this.props
    const {
      checks: { data },
    } = checkTemplate

    deleteCheck(order) // 删除掉当前的关卡后要获取上一个关卡的数据

    const fetchCheck = async id => {
      const singleCheckData = await getEntityList(
        levelStrategies.SINGLE_CHECK,
        {
          id,
        },
      )
      return singleCheckData
    }

    if (data.length > 1 && order > 0) {
      const newOrder = order - 1
      const currentData = await fetchCheck(data[newOrder].id)
      setTasks(currentData, newOrder)
      this.setCurrentCheck(currentData, order - 1)
    } else if (data.length > 1 && order === 0) {
      const newOrder = order + 1
      const currentData = await fetchCheck(data[newOrder].id)
      setTasks(currentData, newOrder)
      this.setCurrentCheck(currentData, order)
    } else {
      this.setCurrentCheck(null, -1)
    }
  }

  addCheck = (type): void => {
    const {
      order,
      levelStore: { addCheck },
    } = this.props
    let check: any = {
      id: Date.now() + 'new',
      title: '',
      type,
      tasks: [],
      pass_days: 0,
    }

    if (type === checkType.integration) {
      check = {
        ...check,
        configs: {
          video_time_length: 0,
          video_count: 0,
        },
      }
    } else if (type === checkType.text) {
      check = {
        ...check,
        text: {
          text: '',
        },
        configs: {},
      }
    } else {
      check = {
        ...check,
        configs: {},
      }
    }

    addCheck(check, order + 1)
  }

  toggleGroupModal = () => {
    const {
      levelStore: { toggleGroupModal },
    } = this.props
    toggleGroupModal(true)
  }

  emptyGroups = () => {
    confirm({
      title: '清空分组提醒',
      okType: 'danger',
      content: '该功能会清空所有关卡的分组设置，是否清空？',
      onOk: () => {
        const {
          levelStore: { setCheckTemplate, checkTemplate },
          form,
        } = this.props
        const {
          checks: { meta, data },
        } = checkTemplate
        const copiedData = cloneDeep(toJS(data))

        copiedData.forEach(d => delete d.group_id)
        form.setFieldsValue({ group_id: null })
        const payload = {
          ...checkTemplate,
          checks: {
            meta,
            data: copiedData,
          },
        }
        setCheckTemplate(payload)
      },
    })
  }

  renderLeftCom = (): React.ReactElement<any> => {
    const {
      globalStore: { staffInfo },
      form,
      data,
      currentLessonId,
    } = this.props
    const { examList, videoList } = this.state
    const { staff_level } = staffInfo
    const { getFieldDecorator } = form
    const examOptions =
      examList &&
      examList.data.map(exam => (
        <Option key={exam.id} value={exam.id}>
          <span>
            ID:{exam.id} {exam.type ? exam.type : ''} 题目:
            {exam.content.length > 22
              ? exam.content.slice(0, 22) + '...'
              : exam.content}
          </span>
        </Option>
      ))

    const isVideoCheck =
      data && data.type === checkType.integration ? true : false
    const videoOptions =
      videoList &&
      videoList.data.map(video =>
        (video.ismanager && staff_level !== '管理员') ||
        (video.ismanager && currentLessonId === 1) ? (
          <Option key={video.id} value={video.id} disabled>
            {withPopover({
              overlayStyle: { zIndex: 10000 },
              content: '该视频仅对管理员可见',
            })(<span>{video.title}</span>)}
          </Option>
        ) : (
          <Option key={video.id} value={video.id}>
            <span>{video.title}</span>
          </Option>
        ),
      )
    const videoDataSource = data ? toJS(data.tasks) : []
    const exampleDataSource =
      (data &&
        data.tasks &&
        data.tasks[0] &&
        toJS(data.tasks[0].example_titles)) ||
      []
    const examDataSource =
      (data &&
        data.tasks &&
        data.tasks[0] &&
        toJS(data.tasks[0].exam_titles)) ||
      []
    let dataSource = isVideoCheck
      ? videoDataSource
      : data.type === checkType.example
      ? exampleDataSource
      : examDataSource
    dataSource = dataSource ? dataSource : []
    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: '题目',
        key: 'name',
        render: (text, record) => {
          return withPopover({
            placement: 'bottom',
            content: (
              <span
                style={{
                  verticalAlign: 'bottom',
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    isVideoCheck &&
                    record &&
                    record.example_titles &&
                    record.example_titles.length > 0
                      ? '复习与总结'
                      : record.content || record.name,
                }}
              />
            ),
          })(
            <span
              className={styles.ellipsis}
              style={{
                verticalAlign: 'bottom',
              }}
              dangerouslySetInnerHTML={{
                __html:
                  isVideoCheck &&
                  record &&
                  record.example_titles &&
                  record.example_titles.length > 0
                    ? '复习与总结'
                    : record.content || record.name,
              }}
            />,
          )
        },
      },
      {
        title: '类型',
        key: 'type',
        dataIndex: 'type',
        render: (text, record) => (
          <span
            style={{
              verticalAlign: 'bottom',
              backgroundImage: `url(${
                videoTypeIconMap[isVideoCheck ? record.type : taskType.example]
              })`,
              display: 'inline-block',
              width: 18,
              height: 18,
              marginRight: 6,
              marginTop: 3,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
            }}
          />
        ),
      },
      {
        title: '操作',
        key: 'operation',
        render: (text, record) => (
          <div className={styles.tableOperation}>
            <i
              className={styles.tableDeleteOperation}
              onClick={this.deleteTask.bind(this, record.id)}
            />
            <i
              className={styles.tableDownOperation}
              onClick={this.reorderTask.bind(this, record.id, 'down')}
            />
            <i
              className={styles.tableUpOperation}
              onClick={this.reorderTask.bind(this, record.id, 'up')}
            />
          </div>
        ),
      },
    ]
    return (
      <Fragment>
        <FormItem
          className={styles.configCardFormItem}
          {...leftWithLabelLayout}
          label={`关卡名称`}
        >
          {getFieldDecorator('name', {
            initialValue: data.name ? data.name : '',
            rules: [
              {
                type: 'string',
                required: true,
                message: '请输入名称',
              },
            ],
          })(<Input onChange={this.updateCheck} />)}
        </FormItem>
        <FormItem
          className={styles.configCardFormItem}
          {...leftWithLabelLayout}
          label={`关卡描述`}
        >
          {getFieldDecorator('description', {
            initialValue: data.description ? data.description : '',
            rules: [
              {
                type: 'string',
                required: true,
                message: '请输入关卡描述',
              },
            ],
          })(<Input onChange={this.updateCheck} />)}
        </FormItem>
        {data.type === checkType.integration && (
          <>
            <FormItem
              className={styles.configCardFormItem}
              label={`视频任务`}
              {...leftWithLabelLayout}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder={`请输入要添加的视频内容`}
                className={styles.select}
                defaultActiveFirstOption={false}
                dropdownRender={menu => (
                  <div>
                    {menu}
                    <div
                      style={{
                        visibility: this.state.loading ? 'visible' : 'hidden',
                        textAlign: 'center',
                        fontSize: 12,
                        color: '#1890ff',
                      }}
                    >
                      <Icon type="loading" />
                    </div>
                  </div>
                )}
                showArrow={false}
                filterOption={false}
                onSearch={this.onSelectSearch.bind(this, 'video')}
                onSelect={this.onSelect.bind(this, 'video')}
                onPopupScroll={this.onPopupScroll.bind(this, 'video')}
                notFoundContent={null}
                onDeselect={this.onDeselect}
              >
                {videoOptions}
              </Select>
            </FormItem>
          </>
        )}
        {data.type !== checkType.text && (
          <Fragment>
            <FormItem
              className={styles.configCardFormItem}
              label={`试题任务`}
              {...leftWithLabelLayout}
            >
              <Select
                mode="multiple"
                showSearch
                className={styles.select}
                placeholder={`请输入要添加的试题内容`}
                dropdownRender={menu => (
                  <div>
                    {menu}
                    <div
                      style={{
                        visibility: this.state.loading ? 'visible' : 'hidden',
                        textAlign: 'center',
                        fontSize: 12,
                        color: '#1890ff',
                      }}
                    >
                      <Icon type="loading" />
                    </div>
                  </div>
                )}
                defaultActiveFirstOption={false}
                showArrow={false}
                filterOption={false}
                onSearch={this.onSelectSearch.bind(this, 'exam')}
                onSelect={this.onSelect.bind(this, 'exam')}
                notFoundContent={null}
                onPopupScroll={this.onPopupScroll.bind(this, 'exam')}
                onDeselect={this.onDeselect}
              >
                {examOptions}
              </Select>
            </FormItem>
          </Fragment>
        )}
        {data.type === checkType.integration && (
          <FormItem
            className={styles.configCardFormItem}
            {...leftWithLabelLayout}
            label={`工作指引`}
          >
            {getFieldDecorator('guide', {
              initialValue: '',
              rules: [
                {
                  type: 'string',
                  message: '请输入要添加的工作指引文字',
                },
              ],
            })(
              <Input
                addonAfter={<Icon type="plus" onClick={this.addTextTask} />}
              />,
            )}
          </FormItem>
        )}
        {data.type !== checkType.text && (
          <FormItem {...leftWithouLabelLayout}>
            <Table
              rowKey={'id'}
              columns={columns}
              sortDirections={['ascend']}
              expandedRowRender={
                isVideoCheck
                  ? record => {
                      const { example_titles } = record
                      if (
                        record.type === taskType.example &&
                        (example_titles && example_titles.length > 0)
                      ) {
                        return (
                          <div>
                            {example_titles &&
                              example_titles.map(title => (
                                <div
                                  key={title.id}
                                  className={styles.tableExpandedRow}
                                >
                                  <span>ID：{title.id}</span>
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: ` 题目：${title.content}`,
                                    }}
                                  />
                                  <br />
                                </div>
                              ))}
                          </div>
                        )
                      }
                      return null
                    }
                  : null
              }
              dataSource={dataSource}
              footer={() => ''}
              pagination={false}
              scroll={dataSource.length > 4 ? { y: 260 } : {}}
            />
          </FormItem>
        )}
        {data.type === checkType.text && (
          <FormItem
            className={`${styles.configCardFormItem}`}
            {...leftEditorLayout}
          >
            {data.tasks ? (
              <RichEditor
                defaultValue={
                  data.tasks && data.tasks.length > 0
                    ? data.tasks[0].text.text
                    : ''
                }
                className={styles.configCardEditor}
                onContentChange={this.updateCheck}
              />
            ) : null}
          </FormItem>
        )}
      </Fragment>
    )
  }

  renderRightCom = (): React.ReactElement<any> => {
    const {
      form,
      data,
      order,
      levelStore: {
        checkTemplate: { checks, check_groups },
      },
    } = this.props
    const { getFieldDecorator } = form

    return (
      <Fragment>
        {data.type !== checkType.example && (
          <FormItem
            className={styles.configCardFormItem}
            {...rightWithLabelLayout}
            label={`通关天数`}
          >
            {getFieldDecorator('pass_days', {
              initialValue: data.pass_days ? Number(data.pass_days) : 0,
              rules: [
                {
                  type: 'number',
                },
              ],
            })(
              <InputNumber
                className={styles.inputNumber}
                onChange={this.updateCheck}
                placeholder={'通关天数（非必选）'}
              />,
            )}
          </FormItem>
        )}
        {data.type === checkType.integration && (
          <Fragment>
            <FormItem
              className={styles.configCardFormItem}
              {...rightWithLabelLayout}
              label={`视频数量限制`}
            >
              {getFieldDecorator('video_count', {
                initialValue: data.configs ? data.configs.video_count : 0,
              })(
                <InputNumber
                  className={styles.inputNumber}
                  onChange={this.updateCheck}
                  placeholder={'视频数量限制（非必选）'}
                />,
              )}
            </FormItem>
            <FormItem
              className={styles.configCardFormItem}
              {...rightWithLabelLayout}
              label={`视频总时长限制`}
            >
              {getFieldDecorator('video_time_length', {
                initialValue: data.configs ? data.configs.video_time_length : 0,
              })(
                <InputNumber
                  className={styles.inputNumber}
                  onChange={this.updateCheck}
                  placeholder={'视频总时长限制（非必选）'}
                />,
              )}
            </FormItem>
          </Fragment>
        )}
        <Row type="flex">
          <Col span={14}>
            <FormItem
              className={styles.configCardFormItem}
              {...rightWithLabelLayout}
              labelCol={{ sm: 10, xs: 24 }}
              wrapperCol={{ sm: 14, xs: 24 }}
              label={`选择分组`}
              style={{ position: 'relative', left: 4 }}
            >
              {getFieldDecorator('group_id', {
                initialValue: data.group_id ? data.group_id : null,
              })(
                <Select
                  placeholder={'请选择关卡分组'}
                  onSelect={this.updateCheckGroup}
                >
                  <Option value={null}>无</Option>
                  {check_groups &&
                    check_groups.map(group => (
                      <Option key={group.id} value={group.id}>
                        {group.name}
                      </Option>
                    ))}
                </Select>,
              )}
            </FormItem>
          </Col>
          <Col span={4} offset={1}>
            <Button
              style={{ marginTop: 3, width: '100%' }}
              onClick={this.toggleGroupModal}
            >
              <Icon type="plus" />
              分组
            </Button>
          </Col>
          <Col span={4} offset={1}>
            <Button
              style={{ marginTop: 3, width: '100%' }}
              onClick={this.emptyGroups}
            >
              清空分组
            </Button>
          </Col>
        </Row>
        <Row
          className={styles.configCardBtnGroup}
          type="flex"
          justify="space-between"
        >
          <Col span={6} className={styles.customLabel}>
            新增：
          </Col>
          <Col span={18}>
            <Row type="flex" justify="space-between">
              <Col span={5}>
                <Button
                  onClick={this.addCheck.bind(this, checkType.integration)}
                >
                  <Icon type="plus" />
                  {checkType.integration}
                </Button>
              </Col>
              <Col span={5}>
                <Button onClick={this.addCheck.bind(this, checkType.example)}>
                  <Icon type="plus" />
                  {checkType.example}
                </Button>
              </Col>
              <Col span={5}>
                <Button onClick={this.addCheck.bind(this, checkType.exam)}>
                  <Icon type="plus" />
                  {checkType.exam}
                </Button>
              </Col>
              <Col span={5}>
                <Button onClick={this.addCheck.bind(this, checkType.text)}>
                  <Icon type="plus" />
                  {checkType.text}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row
          className={styles.configCardBtnGroup}
          type="flex"
          justify="space-between"
        >
          <Col span={6} className={styles.customLabel}>
            设置：
          </Col>
          <Col span={18}>
            <Row type="flex" justify="space-between">
              <Col span={7}>
                <Button
                  disabled={order === 0 ? true : false}
                  onClick={this.reorderCheck.bind(this, order - 1, order)}
                >
                  升
                </Button>
              </Col>
              <Col span={7}>
                <Button
                  disabled={
                    checks &&
                    (checks.data.length === 1 ||
                      checks.data.length - 1 === order)
                      ? true
                      : false
                  }
                  onClick={this.reorderCheck.bind(this, order + 1, order)}
                >
                  降
                </Button>
              </Col>
              <Col span={7}>
                <Button onClick={this.deleteCheck.bind(this, order)}>删</Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Fragment>
    )
  }

  render() {
    const { order, data } = this.props

    return (
      <Form className={`${styles.configCard}`}>
        <Row>
          <Col
            span={12}
            style={{
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            {order + 1}# {data.type}
          </Col>
        </Row>
        <Row>
          <Col span={12}>{this.renderLeftCom()}</Col>
          <Col span={12} className={styles.configCardRightCom}>
            {this.renderRightCom()}
          </Col>
        </Row>
      </Form>
    )
  }
}
const ConfigCard: any = Form.create()(JZConfigCard)

export default ConfigCard
